import React, {useCallback, useEffect, useRef, useState} from 'react';
import {App, Checkbox, Modal, Spin, Tooltip} from 'antd';
import {ResearchReportBody} from './components/ResearchReportBody';
import {
    generateResearchReport,
    getResearchReportDetail,
    getResearchReportLatest,
    getResearchReportList,
    getResearchReportSymbols,
} from '../../../services/researchReportService';
import {
    getAvailableSymbols,
    getUserHedgeConfig,
    parseSymbolList,
    updateUserHedgeConfig,
} from '../../../services/hedgeService';
import type {ResearchReportDetail, ResearchReportSummary,} from '../../../types/researchReport';
// 复用 pattern 页的变量体系与基础控件样式，保证与 K 线形态页外观一致
import '../PatternRecognitionPage.css';
import './ResearchReportPage.css';

const formatTime = (ts?: number): string =>
    null == ts ? '--' : new Date(ts).toLocaleString('zh-CN', {hour12: false});

/**
 * 研报页面（终端式布局，外观对齐 K 线形态页）：
 * 顶部通栏工具栏（品种标题+数据截至提示+生成按钮），
 * 中间报告全文滚动区，右侧面板（品种网格 + 历史研报时间线）。
 */
export const ResearchReportPage: React.FC = () => {
    const {message} = App.useApp();
    const pageRef = useRef<HTMLDivElement>(null);
    const [pageHeight, setPageHeight] = useState<number | undefined>(undefined);
    const [symbols, setSymbols] = useState<string[]>([]);
    const [activeSymbol, setActiveSymbol] = useState('BTC');
    const [history, setHistory] = useState<ResearchReportSummary[]>([]);
    const [detail, setDetail] = useState<ResearchReportDetail | null>(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [generating, setGenerating] = useState(false);
    /** 品种配置弹窗（可见报告品种 ⊆ 系统白名单；对冲品种 ⊆ 可见报告品种） */
    const [configOpen, setConfigOpen] = useState(false);
    const [availSymbols, setAvailSymbols] = useState<string[]>([]);
    const [selReports, setSelReports] = useState<string[]>([]);
    const [selHedges, setSelHedges] = useState<string[]>([]);
    const [savingConfig, setSavingConfig] = useState(false);

    // 精确测量可用高度：绕过外层 Antd Layout min-height:100vh 导致的 flex stretch 失效（同 pattern 页机制）
    useEffect(() => {
        const el = pageRef.current;
        if (!el) return;
        const calc = () => {
            const top = el.getBoundingClientRect().top;
            setPageHeight(window.innerHeight - top);
        };
        calc();
        const ro = new ResizeObserver(calc);
        ro.observe(el);
        return () => ro.disconnect();
    }, []);

    /** 重载可见品种网格（配置保存后/首载用；空=未配置，展示引导去配置） */
    const reloadSymbols = useCallback(async () => {
        try {
            const list = await getResearchReportSymbols();
            const next = list ?? [];
            setSymbols(next);
            if (next.length) {
                setActiveSymbol(prev => next.includes(prev) ? prev : next[0]);
            } else {
                setActiveSymbol('');
                setDetail(null);
                setHistory([]);
            }
            return next;
        } catch (e: any) {
            setSymbols([]);
            setActiveSymbol('');
            setError(e?.message ?? '品种列表加载失败');
            return [];
        }
    }, []);

    // 品种列表：按当前用户可见配置（空=未配置，不兜底展示未配置品种）
    useEffect(() => {
        void reloadSymbols();
    }, [reloadSymbols]);

    /** 打开品种配置弹窗：加载可选品种（系统白名单）与我的当前配置 */
    const openConfig = useCallback(async () => {
        try {
            const [user, avail] = await Promise.all([getUserHedgeConfig(), getAvailableSymbols()]);
            setAvailSymbols(avail);
            setSelReports(parseSymbolList(user?.reportsAllowedSymbols));
            setSelHedges(parseSymbolList(user?.hedgesAllowedSymbols));
            setConfigOpen(true);
        } catch (e: any) {
            message.error(e?.message ?? '品种配置加载失败');
        }
    }, [message]);

    /** 保存品种配置（保存后重载网格；对冲勾选自动剔除已取消可见的品种） */
    const saveConfig = useCallback(async () => {
        setSavingConfig(true);
        try {
            const hedges = selHedges.filter(s => selReports.includes(s));
            await updateUserHedgeConfig({
                reportsAllowedSymbols: selReports.join(','),
                hedgesAllowedSymbols: hedges.join(','),
            });
            setConfigOpen(false);
            message.success('品种配置已保存');
            await reloadSymbols();
        } catch (e: any) {
            message.error(e?.message ?? '品种配置保存失败');
        } finally {
            setSavingConfig(false);
        }
    }, [selReports, selHedges, message, reloadSymbols]);

    const loadLatest = useCallback(async (symbol: string) => {
        setLoading(true);
        setError('');
        try {
            const data = await getResearchReportLatest(symbol);
            setDetail(data);
            if (null == data) {
                setError('该品种暂无研报，可点击「生成」立即生成一份');
            }
        } catch (e: any) {
            setDetail(null);
            setError(e?.message ?? '研报加载失败');
        } finally {
            setLoading(false);
        }
    }, []);

    const loadHistory = useCallback(async (symbol: string) => {
        try {
            const page = await getResearchReportList(symbol, 0, 30);
            setHistory(page?.content ?? []);
        } catch {
            setHistory([]);
        }
    }, []);

    // 切换品种：拉最新研报 + 历史时间线
    useEffect(() => {
        if (!activeSymbol) return;
        loadLatest(activeSymbol);
        loadHistory(activeSymbol);
    }, [activeSymbol, loadLatest, loadHistory]);

    const handleSelectHistory = async (id: number) => {
        setLoading(true);
        try {
            const data = await getResearchReportDetail(id);
            setDetail(data);
        } catch {
            message.error('研报详情加载失败');
        } finally {
            setLoading(false);
        }
    };

    const handleGenerate = async () => {
        if (generating) return;
        setGenerating(true);
        try {
            const data = await generateResearchReport(activeSymbol);
            setDetail(data);
            message.success(`${activeSymbol} 研报生成成功`);
            loadHistory(activeSymbol);
        } catch (e: any) {
            message.error(e?.message ?? '生成失败，请稍后重试');
        } finally {
            setGenerating(false);
        }
    };

    const content = detail?.content;

    return (
        <div className="pattern-page rr-page" ref={pageRef}
             style={pageHeight ? {height: pageHeight} : undefined}>
            {/* 顶部通栏工具栏 */}
            <div className="rr-toolbar">
                <span className="rr-toolbar-symbol">{activeSymbol} 研报</span>
                <span className="toolbar-sep"/>
                <span className="rr-toolbar-hint">
                    {content?.meta?.dataAsOfText
                        ? `数据截至 ${content.meta.dataAsOfText}`
                        : '现货/永续双口径 · 程序规则自动生成'}
                </span>
                <Tooltip title="生成研报">
                    <button className={`rr-generate-btn${generating ? ' loading' : ''}`}
                            disabled={generating} aria-label="生成研报" onClick={handleGenerate}>
                        ⟳
                    </button>
                </Tooltip>
                <Tooltip title="品种配置">
                    <button className="rr-config-btn" aria-label="品种配置" onClick={openConfig}>⚙</button>
                </Tooltip>
            </div>

            <div className="rr-body">
                {/* 中间报告滚动区 */}
                <main className="rr-content">
                    {0 === symbols.length ? (
                        <div className="rr-empty">
                            <div className="rr-empty-title">暂无可查看品种</div>
                            <div className="rr-empty-desc">
                                请在系统已开启的品种中勾选你想看的报告（未配置时全部不可见）
                            </div>
                            <button className="rr-action-btn" onClick={openConfig}>配置品种</button>
                        </div>
                    ) : null}

                    {0 < symbols.length && loading && !content ? (
                        <div className="rr-loading"><Spin/></div>
                    ) : null}

                    {0 < symbols.length && !loading && !content && error ? (
                        <div className="rr-empty">
                            <div className="rr-empty-title">暂无研报</div>
                            <div className="rr-empty-desc">{error}</div>
                            <button className="rr-action-btn" onClick={handleGenerate} disabled={generating}>
                                立即生成
                            </button>
                        </div>
                    ) : null}

                    {0 < symbols.length && content && detail ? <ResearchReportBody detail={detail}/> : null}
                </main>

                {/* 右侧面板：品种 + 历史研报 */}
                <aside className="rr-panel">
                    <div className="rr-panel-title">品种</div>
                    <div className="rr-symbol-grid">
                        {symbols.map(s => (
                            <button
                                key={s}
                                className={`rr-symbol-item${s === activeSymbol ? ' active' : ''}`}
                                onClick={() => setActiveSymbol(s)}
                            >
                                {s}
                            </button>
                        ))}
                    </div>
                    <div className="rr-panel-title">历史研报</div>
                    <div className="rr-history-list">
                        {history.map(item => (
                            <div
                                key={item.id}
                                className={`rr-history-item${detail?.id === item.id ? ' active' : ''}`}
                                onClick={() => handleSelectHistory(item.id)}
                            >
                                <span className={`rr-status-dot${'FAILED' === item.status ? ' failed' : ''}`}/>
                                <span className="rr-history-time">{formatTime(item.generatedAt)}</span>
                                <span className="rr-history-price">
                                    {'SUCCESS' === item.status && null != item.spotPrice ? `$${item.spotPrice}` : '失败'}
                                </span>
                            </div>
                        ))}
                        {!history.length && <div className="rr-history-empty">暂无历史研报</div>}
                    </div>
                </aside>
            </div>

            <Modal
                title="我的品种配置"
                open={configOpen}
                onOk={saveConfig}
                onCancel={() => setConfigOpen(false)}
                okText="保存"
                cancelText="取消"
                confirmLoading={savingConfig}
            >
                <div style={{marginBottom: 10, fontSize: 13, color: 'var(--pt-text-dim, #888)'}}>
                    可见报告品种只能从「系统已开启」中选择；对冲品种必须包含在可见报告品种内（没有报告就不会触发对冲）。
                </div>
                <div style={{marginBottom: 6, fontWeight: 500}}>可见报告品种</div>
                {availSymbols.length ? (
                    <Checkbox.Group
                        value={selReports}
                        options={availSymbols.map(s => ({label: s, value: s}))}
                        onChange={(values) => {
                            const next = values as string[];
                            setSelReports(next);
                            // 取消可见时同步剔除对冲勾选（后端保存亦会拒绝越界）
                            setSelHedges(prev => prev.filter(s => next.includes(s)));
                        }}
                    />
                ) : (
                    <div style={{color: 'var(--pt-text-dim, #888)'}}>
                        系统暂未开启任何品种的报告生成，请联系管理员在「运营配置 → 报告品种配置」中开启
                    </div>
                )}
                <div style={{margin: '14px 0 6px', fontWeight: 500}}>对冲品种（勾选=自动对冲）</div>
                {selReports.length ? (
                    <Checkbox.Group
                        value={selHedges.filter(s => selReports.includes(s))}
                        options={selReports.map(s => ({label: s, value: s}))}
                        onChange={(values) => setSelHedges(values as string[])}
                    />
                ) : (
                    <div style={{color: 'var(--pt-text-dim, #888)'}}>请先勾选可见报告品种</div>
                )}
            </Modal>
        </div>
    );
};

export default ResearchReportPage;
